'use client';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Pagination } from '@/components/ui/Pagination';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Search, X, ChevronRight, CalendarDays, ChevronDown, LayoutGrid, SlidersHorizontal } from 'lucide-react';
import type { PreorderItem } from '@/lib/api';
import { LanguageFilter, applyLangFilter, ALL_LANG_TYPES, LANG_LIST, langName, type LangType } from '@/components/catalog/LanguageFilter';
import { OfferFilter, readOfferParams, offerLinkSuffix, OFFER_MULTS, OFFER_ICONS, ALL_REGIONS, type OfferType, type RegionCode } from '@/components/catalog/OfferFilter';
import { GENRES } from '@/components/catalog/GenreFilter';
import { SortDropdown } from '@/components/SortDropdown';
import { fetchPrices } from '@/lib/priceCache';
import { usePriceConvert } from '@/lib/PriceContext';
import { useT } from '@/hooks/useT';
import { gameCount } from '@/lib/i18n';

const PAGE_SIZE = 24;

// Фонове довантаження повного preorder-індексу (SSR віддає лише перші 200 для швидкого TTFB).
let _cache: PreorderItem[] | null = null;
let _promise: Promise<PreorderItem[]> | null = null;
function loadFullIndex(): Promise<PreorderItem[]> {
  if (_cache) return Promise.resolve(_cache);
  if (_promise) return _promise;
  _promise = fetch('/api/preorder-index', { credentials: 'same-origin' })
    .then(r => r.json())
    .then((data: PreorderItem[]) => { _cache = data; return data; })
    .catch(() => []);
  return _promise;
}

const PL_TAG: Record<string, { label: string; cls: string; svg?: boolean }> = {
  XBOXSERIESX: { label: 'X|S',    cls: 'ptag-xsx' },
  XBOXONE:     { label: 'ONE',    cls: 'ptag-xone' },
  PC:          { label: 'PC',     cls: 'ptag-pc' },
  XCLOUD:      { label: 'xCloud', cls: 'ptag-cloud', svg: true },
};
const PLAT_CHIP_ON: Record<string, string> = {
  XBOXSERIESX: 'on-xsx',
  XBOXONE:     'on-xone',
  PC:          'on-pc',
  XCLOUD:      'on-xcloud',
};
const CloudIcon = () => (
  <svg viewBox="0 0 32 22" width="13" height="9" fill="currentColor" aria-label="Xbox Cloud Gaming" style={{ display: 'block' }}>
    <path d="M26 9a6 6 0 0 0-1-.08A8 8 0 0 0 10.1 7.4 5 5 0 1 0 5 17h21a5 5 0 1 0 0-10c-.34 0-.68.03-1 .08A5.99 5.99 0 0 0 26 9z"/>
  </svg>
);
const PL_NAMES: Record<string, string> = { XBOXSERIESX: 'Xbox Series X|S', XBOXONE: 'Xbox One', PC: 'PC' };


function fmtPrice(price: number | null, cur: string | null) {
  if (!price) return null;
  return new Intl.NumberFormat('uk-UA', { style: 'currency', currency: cur || 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price);
}

function fmtDateShort(d: string, lang: string) {
  const loc = lang === 'ru' ? 'ru-RU' : lang === 'uk' ? 'uk-UA' : 'en';
  return new Date(d).toLocaleDateString(loc, { day: 'numeric', month: 'short' });
}

// Локалізована назва країни за ISO-кодом регіону (AR→Аргентина за мовою сайту).
function regionName(code: string, lang: string): string {
  try {
    const loc = lang === 'ru' ? 'ru-RU' : lang === 'uk' ? 'uk-UA' : 'en';
    return new Intl.DisplayNames([loc], { type: 'region' }).of(code) ?? code;
  } catch { return code; }
}

function GameCard({ item, lang, offerType = 'key', region = 'any', priceOverride }: { item: PreorderItem; lang: string; offerType?: OfferType; region?: RegionCode; priceOverride?: number | null }) {
  const { convert: fmtConverted } = usePriceConvert();
  const t = useT();
  const minOfferUsd =
    offerType === 'gift'    ? item.min_gift_usd
    : offerType === 'account' ? item.min_account_usd
    : offerType === 'rental'  ? item.min_rental_usd
    : item.min_key_usd;
  const baseUsd = priceOverride !== undefined
    ? priceOverride
    : minOfferUsd ?? (item.list_price != null ? item.list_price * OFFER_MULTS[offerType] : null);
  const price = fmtConverted(baseUsd) ?? fmtPrice(baseUsd, 'USD');
  const discount = item.discount_pct && item.discount_pct > 0 ? Math.round(item.discount_pct) : null;
  const platforms = item.platforms.map(p => PL_TAG[p]).filter(Boolean) as { label: string; cls: string; svg?: boolean }[];
  const displayTitle = (lang === 'ru' && item.title_ru) ? item.title_ru
    : (lang === 'uk' && (item as any).title_uk) ? (item as any).title_uk
    : item.title;
  const shortDesc = (lang === 'ru' && item.short_description_ru) ? item.short_description_ru
    : (lang === 'uk' && (item as any).short_description_uk) ? (item as any).short_description_uk
    : item.short_description;
  const fullDesc = (lang === 'ru' && item.description_ru) ? item.description_ru
    : item.description_en;
  const displayDesc = shortDesc || (fullDesc ? fullDesc.slice(0, 120).trimEnd() + (fullDesc.length > 120 ? '…' : '') : '');

  return (
    <Link href={`/product/${item.product_id}${offerLinkSuffix(offerType, region)}`} className="po2-card">
      <div className="po2-card-art">
        {item.poster
          ? <img src={item.poster} alt={displayTitle} loading="lazy" />
          : <div className="po2-card-art-empty" />
        }

        {/* date chip — top-left (like catalog) */}
        <span className="po2-card-date-chip">
          <CalendarDays size={9} aria-hidden />
          {fmtDateShort(item.release_at, lang)}
        </span>

        {/* discount badge — top-right */}
        {discount && (
          <span className="po2-card-discount">-{discount}%</span>
        )}

        {/* platform tags — bottom-left, catalog style */}
        {platforms.length > 0 && (
          <div className="cat-platforms">
            {platforms.map(p => (
              <span key={p.label} className={`platform-tag ${p.cls}`}>{p.svg ? <CloudIcon /> : p.label}</span>
            ))}
          </div>
        )}

        <div className="po2-card-overlay">
          <span className="po2-card-overlay-btn">{t('hero_more')} <ChevronRight size={12} /></span>
        </div>
      </div>

      <div className="po2-card-body">
        <p className="po2-card-title">{displayTitle}</p>
        <p className="po2-card-desc">{displayDesc ?? ''}</p>
        <div className="po2-card-footer">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            {price
              ? <span className="po2-card-price">{price}</span>
              : <span className="po2-card-tba">TBA</span>
            }
            {discount != null && item.msrp != null && (
              <span className="cat-price-orig">{fmtConverted(item.msrp) ?? fmtPrice(item.msrp, item.currency_code)}</span>
            )}
          </div>
          <ChevronRight size={13} color="var(--fg-4)" />
        </div>
      </div>
    </Link>
  );
}

export function PreorderGrid({ items, totalCount, excludeId }: { items: PreorderItem[]; totalCount?: number; excludeId?: string }) {
  const searchParams = useSearchParams();
  const { lang } = usePriceConvert();
  const t = useT();

  const [fullIndex,  setFullIndex]  = useState<PreorderItem[]>(_cache ?? []);
  const [indexReady, setIndexReady] = useState(_cache !== null);

  useEffect(() => {
    if (indexReady) return;
    loadFullIndex().then(data => { setFullIndex(data); setIndexReady(true); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeItems = useMemo(() => {
    if (!indexReady) return items;
    const initIds = new Set(items.map(i => i.product_id));
    return [
      ...items,
      ...fullIndex.filter(i => !initIds.has(i.product_id) && i.product_id !== excludeId),
    ];
  }, [indexReady, fullIndex, items, excludeId]);
  const SORT_OPTIONS = [
    { value: 'date_asc',   label: t('sort_date_asc') },
    { value: 'date_desc',  label: t('sort_date_desc') },
    { value: 'price_asc',  label: t('sort_price_asc') },
    { value: 'price_desc', label: t('sort_price_desc') },
    { value: 'name',       label: 'A–Z' },
  ];

  const [search, setSearch]     = useState('');
  const [typeFilter, setTypeFilter] = useState<'all'|'game'|'dlc'>('all');
  const [genre, setGenre]       = useState('');
  const [genreRowOpen, setGenreRowOpen] = useState(false);
  const [platform, setPlatform] = useState<string | null>(null);
  const [sort, setSort]         = useState('date_asc');
  const [langFilter, setLangFilter] = useState<string[]>([]);
  const [langTypes,  setLangTypes]  = useState<LangType[]>(ALL_LANG_TYPES);
  const [visible, setVisible]   = useState(1); // номер сторінки
  const gridRef = useRef<HTMLDivElement>(null);
  const [offerType, setOfferType] = useState<OfferType>('key');
  const [region,    setRegion]    = useState<RegionCode>('any');
  const [priceMap,  setPriceMap]  = useState<Record<string, number>>({});

  // -- Mobile bottom-sheet --
  const [sheetOpen, setSheetOpen] = useState(false);
  const [openSec, setOpenSec] = useState<Set<string>>(() => new Set(['sort', 'type']));
  const toggleSec = (id: string) => setOpenSec(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const chip = (active: boolean) => `va-chip${active ? ' on' : ''}`;
  const Sec = (id: string, label: string, badge: number, content: React.ReactNode) => (
    <div className={`fbar-acc${openSec.has(id) ? ' open' : ''}`}>
      <button className="fbar-acc-head" onClick={() => toggleSec(id)}>
        <span className="fbar-acc-title">{label}{badge > 0 && <span className="fbar-acc-badge">{badge}</span>}</span>
        <ChevronDown size={18} className="fbar-acc-chev" />
      </button>
      <div className="fbar-acc-body"><div className="fbar-acc-inner">{content}</div></div>
    </div>
  );

  useEffect(() => {
    if (region === 'any') { setPriceMap({}); return; }
    fetchPrices(offerType, region).then(setPriceMap);
  }, [offerType, region]);

  const allPlatforms = useMemo(() => {
    const set = new Set<string>();
    activeItems.forEach(i => i.platforms.forEach(p => { if (PL_TAG[p]) set.add(p); }));
    return [...set];
  }, [activeItems]);

  const filtered = useMemo(() => {
    let r = activeItems;
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(i => {
        const title = (lang === 'ru' && i.title_ru) ? i.title_ru
          : (lang === 'uk' && (i as any).title_uk) ? (i as any).title_uk
          : i.title;
        return title.toLowerCase().includes(q) || (i.publisher_name ?? '').toLowerCase().includes(q);
      });
    }
    if (typeFilter !== 'all') r = r.filter(i => (i.product_type ?? 'game') === typeFilter);
    if (genre) r = r.filter(i => i.category === genre);
    if (platform) r = r.filter(i => i.platforms.includes(platform));
    r = applyLangFilter(r, langFilter, langTypes);

    return [...r].sort((a, b) => {
      if (sort === 'date_asc')   return a.release_at.localeCompare(b.release_at);
      if (sort === 'date_desc')  return b.release_at.localeCompare(a.release_at);
      if (sort === 'price_asc')  return (a.list_price ?? Infinity) - (b.list_price ?? Infinity);
      if (sort === 'price_desc') return (b.list_price ?? 0) - (a.list_price ?? 0);
      if (sort === 'name')       return a.title.localeCompare(b.title);
      return 0;
    });
  }, [activeItems, search, typeFilter, genre, platform, sort, lang, langFilter, langTypes]);

  const filtersActive = search.trim() !== '' || typeFilter !== 'all' || genre !== '' || platform !== null || langFilter.length > 0;
  const knownTotal = (!indexReady && !filtersActive) ? (totalCount ?? filtered.length) : filtered.length;
  const totalPages = Math.max(1, Math.ceil(knownTotal / PAGE_SIZE));
  const safePage   = Math.min(visible, totalPages);
  const shown      = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const clearSearch = useCallback(() => setSearch(''), []);

  const resetVisible = () => setVisible(1);
  function goToPage(n: number) {
    setVisible(n);
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const activeCount = [
    typeFilter !== 'all', platform !== null, genre !== '',
    langFilter.length > 0, offerType !== 'key', region !== 'any',
  ].filter(Boolean).length;

  const resetAll = () => {
    setSearch(''); setTypeFilter('all'); setPlatform(null); setGenre('');
    setLangFilter([]); setLangTypes(ALL_LANG_TYPES);
    setOfferType('key'); setRegion('any');
    resetVisible();
  };

  return (
    <div className="po2-body">
      {/* Header */}
      <div className="po2-body-header">
        <h2 className="po2-body-title">{t('preorder_title')}</h2>
        <span className="po2-body-count">
          {filtersActive
            ? `${filtered.length} ${t('common_of')} ${knownTotal}`
            : gameCount(knownTotal, lang)
          }
        </span>
      </div>

      {/* Mobile filter trigger (bottom-sheet) */}
      <div className="fbar-mobile">
        <button className={`fbar-m-btn${activeCount > 0 ? ' on' : ''}`} onClick={() => setSheetOpen(true)}>
          <SlidersHorizontal size={15} /> {t('m_filters')}
          {activeCount > 0 && <span className="fbar-m-badge">{activeCount}</span>}
        </button>
        <span className="fbar-m-count">{filtered.length}</span>
      </div>

      {/* Filter bar */}
      <div className="po2-filters">
        {/* Search */}
        <div className="po2-search-wrap">
          <Search size={14} className="po2-search-icon" aria-hidden />
          <input
            className="po2-search"
            type="search"
            placeholder={t('preorder_search')}
            value={search}
            onChange={e => { setSearch(e.target.value); resetVisible(); }}
            aria-label={t('preorder_search')}
          />
          {search && (
            <button className="po2-search-clear" onClick={clearSearch} aria-label={t('aria_search_clear')}>
              <X size={13} />
            </button>
          )}
        </div>

        {/* Type chips */}
        <div className="po2-platform-chips">
          <span className="po2-flabel">{t('filter_type')}</span>
          <button className={typeFilter === 'all' ? 'va-chip on' : 'va-chip'}
            onClick={() => { setTypeFilter('all'); resetVisible(); }}>
            {t('catalog_type_all')}
          </button>
          <button className={typeFilter === 'game' ? 'va-chip on' : 'va-chip'}
            onClick={() => { setTypeFilter('game'); resetVisible(); }}>
            {t('catalog_type_game')}
          </button>
          <button className={typeFilter === 'dlc' ? 'va-chip on' : 'va-chip'}
            onClick={() => { setTypeFilter('dlc'); resetVisible(); }}>
            {t('catalog_type_dlc')}
          </button>
        </div>

        <div className="po2-sep" />

        {/* Platform chips */}
        <div className="po2-platform-chips">
          <span className="po2-flabel">{t('filter_platform')}</span>
          <button
            className={!platform ? 'va-chip on' : 'va-chip'}
            onClick={() => { setPlatform(null); resetVisible(); }}
          >
            {t('platform_all')}
          </button>
          {allPlatforms.map(p => (
            <button
              key={p}
              className={platform === p ? `va-chip ${PLAT_CHIP_ON[p] ?? 'on'}` : 'va-chip'}
              onClick={() => { setPlatform(platform === p ? null : p); resetVisible(); }}
            >
              {PL_NAMES[p] ?? p}
            </button>
          ))}
        </div>

        <div className="po2-sep" />

        {/* Language filter */}
        <LanguageFilter
          langs={langFilter}
          types={langTypes}
          onChange={(l, lt) => { setLangFilter(l); setLangTypes(lt); resetVisible(); }}
        />
        <OfferFilter basePath="/preorder" onOfferChange={(o, r) => { setOfferType(o); setRegion(r); resetVisible(); }} />

        {/* Genre toggle — reveals an expandable chip row below (catalog style) */}
        <button
          className={`va-chip${genreRowOpen || genre !== '' ? ' on' : ''}`}
          onClick={() => setGenreRowOpen(o => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
        >
          {t('filter_genres')}
          <ChevronDown size={10} style={{ transition: 'transform .2s ease', transform: genreRowOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
        </button>

        {/* Sort */}
        <div style={{ marginLeft: 'auto' }}>
          <SortDropdown
            options={SORT_OPTIONS}
            value={sort}
            onChange={v => { setSort(v); resetVisible(); }}
            ariaLabel={t('sort_aria')}
          />
        </div>
      </div>

      {/* Expandable genre row (catalog style) */}
      <div className={`fbar-genre-expand${genreRowOpen ? ' open' : ''}`}>
        <div className="fbar-row fbar-genre-row-inner">
          <button
            className={`va-chip${genre === '' ? ' on' : ''}`}
            onClick={() => { setGenre(''); resetVisible(); }}
            style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
          >
            <LayoutGrid size={11} strokeWidth={2} />
            {t('filter_all_genres')}
          </button>
          {GENRES.map(({ key, i18nKey, Icon }) => (
            <button
              key={key}
              className={`va-chip${genre === key ? ' on' : ''}`}
              onClick={() => { setGenre(genre === key ? '' : key); resetVisible(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}
            >
              <Icon size={11} strokeWidth={2} />
              {t(i18nKey as never)}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile filter bottom-sheet */}
      <div className={`fbar-scrim${sheetOpen ? ' open' : ''}`} onClick={() => setSheetOpen(false)} />
      <div className={`fbar-sheet${sheetOpen ? ' open' : ''}`} role="dialog" aria-modal="true" aria-label={t('m_filters')}>
        <div className="fbar-sheet-grip" />
        <div className="fbar-sheet-head">
          <span className="fbar-sheet-title">{t('m_filters')}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {activeCount > 0 && <button className="fbar-sheet-reset" onClick={resetAll}>{t('m_reset')} ({activeCount})</button>}
            <button className="fbar-sheet-x" aria-label="✕" onClick={() => setSheetOpen(false)}><X size={16} /></button>
          </div>
        </div>
        <div className="fbar-sheet-body">
          {/* Search */}
          <div className="fbar-quick">
            <div className="fbar-studio-mobile">
              <Search size={15} />
              <input
                placeholder={t('preorder_search')}
                value={search}
                onChange={e => { setSearch(e.target.value); resetVisible(); }}
                autoComplete="off"
              />
            </div>
          </div>
          {Sec('sort', t('m_sort'), 0, (
            <div className="fbar-group-chips">
              {SORT_OPTIONS.map(o => (
                <button key={o.value} className={chip(sort === o.value)} onClick={() => { setSort(o.value); resetVisible(); }}>{o.label}</button>
              ))}
            </div>
          ))}
          {Sec('type', t('filter_type'), typeFilter !== 'all' ? 1 : 0, (
            <div className="fbar-group-chips">
              <button className={chip(typeFilter === 'all')} onClick={() => { setTypeFilter('all'); resetVisible(); }}>{t('catalog_type_all')}</button>
              <button className={chip(typeFilter === 'game')} onClick={() => { setTypeFilter('game'); resetVisible(); }}>{t('catalog_type_game')}</button>
              <button className={chip(typeFilter === 'dlc')} onClick={() => { setTypeFilter('dlc'); resetVisible(); }}>{t('catalog_type_dlc')}</button>
            </div>
          ))}
          {Sec('platform', t('filter_platform'), platform !== null ? 1 : 0, (
            <div className="fbar-group-chips">
              <button className={chip(!platform)} onClick={() => { setPlatform(null); resetVisible(); }}>{t('platform_all')}</button>
              {allPlatforms.map(p => (
                <button key={p} className={platform === p ? `va-chip ${PLAT_CHIP_ON[p] ?? 'on'}` : 'va-chip'}
                  onClick={() => { setPlatform(platform === p ? null : p); resetVisible(); }}>
                  {PL_NAMES[p] ?? p}
                </button>
              ))}
            </div>
          ))}
          {Sec('genre', t('filter_genres'), genre !== '' ? 1 : 0, (
            <div className="fbar-group-chips">
              <button className={chip(genre === '')} onClick={() => { setGenre(''); resetVisible(); }} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <LayoutGrid size={12} /> {t('filter_all_genres')}
              </button>
              {GENRES.map(({ key, i18nKey, Icon }) => (
                <button key={key} className={chip(genre === key)} onClick={() => { setGenre(genre === key ? '' : key); resetVisible(); }} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon size={12} /> {t(i18nKey as never)}
                </button>
              ))}
            </div>
          ))}
          {Sec('offer', t('m_offer'), offerType !== 'key' ? 1 : 0, (
            <div className="fbar-group-chips">
              {(['key', 'gift', 'account', 'rental'] as OfferType[]).map(o => (
                <button key={o} className={chip(offerType === o)} onClick={() => { setOfferType(o); resetVisible(); }} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {OFFER_ICONS[o]} {t(`offer_${o}` as never)}
                </button>
              ))}
            </div>
          ))}
          {Sec('region', t('m_region'), region !== 'any' ? 1 : 0, (
            <div className="fbar-group-chips">
              {ALL_REGIONS.map(r => (
                <button key={r.code} className={chip(region === r.code)} onClick={() => { setRegion(r.code); resetVisible(); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {r.code === 'any' ? t('platform_all') : <>{r.flag} <span suppressHydrationWarning>{regionName(r.code, lang)}</span></>}
                </button>
              ))}
            </div>
          ))}
          {Sec('lang', t('m_lang'), langFilter.length > 0 ? 1 : 0, (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="fbar-group-chips">
                {(['a', 's', 'i'] as LangType[]).map(tk => (
                  <button key={tk} className={chip(langTypes.includes(tk))}
                    onClick={() => { const nt = langTypes.includes(tk) ? langTypes.filter(x => x !== tk) : [...langTypes, tk]; setLangTypes(nt.length ? nt : ALL_LANG_TYPES); resetVisible(); }}>
                    {tk === 'a' ? t('filter_lang_audio') : tk === 's' ? t('filter_lang_subs') : t('filter_lang_interface')}
                  </button>
                ))}
              </div>
              <div className="fbar-group-chips">
                {LANG_LIST.map(l => (
                  <button key={l.code} className={chip(langFilter.includes(l.code))} onClick={() => { const nf = langFilter.includes(l.code) ? langFilter.filter(x => x !== l.code) : [...langFilter, l.code]; setLangFilter(nf); if (langTypes.length === 0) setLangTypes(ALL_LANG_TYPES); resetVisible(); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {l.flag} <span suppressHydrationWarning>{langName(l.code, lang, l.label)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="fbar-sheet-foot">
          <button className="fbar-sheet-apply" onClick={() => setSheetOpen(false)}>
            {t('m_show')} {filtered.length}
          </button>
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="po2-empty">
          <CalendarDays size={40} color="var(--line)" />
          <p>{t('preorder_no_results')}</p>
          <button className="po2-empty-reset" onClick={() => { setSearch(''); setPlatform(null); setTypeFilter('all'); setGenre(''); }}>
            {t('common_reset_filters')}
          </button>
        </div>
      )}

      {/* Grid */}
      {shown.length > 0 && (
        <div ref={gridRef} className="po2-grid">
          {shown.map(item => <GameCard key={item.product_id} item={item} lang={lang} offerType={offerType} region={region} priceOverride={region !== 'any' ? (priceMap[item.product_id] ?? null) : undefined} />)}
        </div>
      )}

      <Pagination page={safePage} totalPages={totalPages} onChange={goToPage} />
    </div>
  );
}
